// Email service for sending bug reports and suggestions
// Uses EmailJS - free tier: 200 emails/month
// Setup instructions:
// 1. Sign up at https://www.emailjs.com/
// 2. Create an email service (Gmail, Outlook, etc.)
// 3. Create an email template
// 4. Copy your Public Key, Service ID, and Template ID
// 5. Update the constants below

const EMAILJS_PUBLIC_KEY = 'O0RGdlL7lGRXaydLD'; // Replace with your EmailJS Public Key
const EMAILJS_SERVICE_ID = 'service_9f7rela'; // Replace with your EmailJS Service ID
const EMAILJS_TEMPLATE_ID = 'template_elpu82i'; // Replace with your EmailJS Template ID
const RECIPIENT_EMAIL = 'cryptoscope.org@gmail.com'; // Replace with your email address

declare global {
  interface Window {
    emailjs?: {
      send: (serviceId: string, templateId: string, templateParams: any) => Promise<any>;
      init: (publicKey: string) => void;
    };
  }
}

export interface EmailReportData {
  type: 'bug' | 'suggestion';
  title: string;
  description: string;
  steps?: string;
  browser: string;
  platform: string;
  timestamp: string;
}

export async function sendReportEmail(reportData: EmailReportData): Promise<boolean> {
  // Check if EmailJS is loaded
  if (!window.emailjs) {
    console.error('EmailJS is not loaded. Please include the EmailJS script in index.html');
    return false;
  }

  // Check if configuration is set
  if (
    !EMAILJS_PUBLIC_KEY ||
    !EMAILJS_SERVICE_ID ||
    !EMAILJS_TEMPLATE_ID ||
    EMAILJS_TEMPLATE_ID.trim() === '' ||
    EMAILJS_PUBLIC_KEY.trim() === '' ||
    EMAILJS_SERVICE_ID.trim() === ''
  ) {
    console.error('EmailJS configuration is not set. Please update emailService.ts with your credentials.');
    alert('Email service is not configured. Reports will be saved locally only.');
    return false;
  }

  try {
    // Initialize EmailJS (only need to do this once, but safe to call multiple times)
    if (!window.emailjs.init) {
      console.error('EmailJS init function not available');
      return false;
    }
    window.emailjs.init(EMAILJS_PUBLIC_KEY);

    // Format the email content
    const emailContent = formatEmailContent(reportData);

    // Prepare template parameters
    // Try multiple parameter name variations for recipient email
    // EmailJS templates can use various parameter names depending on configuration
    const templateParams: any = {
      // Try multiple recipient parameter names (EmailJS may accept different variations)
      to_email: RECIPIENT_EMAIL,
      'to-email': RECIPIENT_EMAIL,
      recipient_email: RECIPIENT_EMAIL,
      recipient: RECIPIENT_EMAIL,
      email: RECIPIENT_EMAIL,
      to: RECIPIENT_EMAIL,
      // Other template parameters
      subject: `${reportData.type === 'bug' ? '🐛 Bug Report' : '💡 Feature Suggestion'}: ${reportData.title}`,
      message: emailContent,
      report_type: reportData.type === 'bug' ? 'Bug Report' : 'Feature Suggestion',
      report_title: reportData.title,
      report_description: reportData.description,
      report_steps: reportData.steps || 'N/A',
      browser: reportData.browser,
      platform: reportData.platform,
      timestamp: reportData.timestamp
    };

    console.log('Sending email with params:', { EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams });

    // Send email (EmailJS v4 API - no public key in send method)
    const result = await window.emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log('Email sent successfully:', result);
    return true;
  } catch (error: any) {
    console.error('Error sending email:', error);
    console.error('Error details:', {
      status: error?.status,
      text: error?.text,
      message: error?.message
    });
    
    // Enhanced error message for recipient email issue
    if (error?.status === 422 && error?.text?.includes('recipients address is empty')) {
      console.error('');
      console.error('❌❌❌ RECIPIENT EMAIL NOT SET IN EMAILJS TEMPLATE! ❌❌❌');
      console.error('');
      console.error('📝 SOLUTION - Follow these steps:');
      console.error('   1. Go to: https://dashboard.emailjs.com/admin/template');
      console.error('   2. Open template: template_elpu82i');
      console.error('   3. Find "To Email" field (at TOP of template editor, NOT in email body)');
      console.error('   4. Set it to: cryptoscope.org@gmail.com');
      console.error('   5. OR set it to: {{to_email}} (with double curly braces)');
      console.error('   6. Click "Save" button');
      console.error('');
      console.error('📄 For detailed step-by-step instructions, see: EMAILJS_SETUP_STEPS.md');
      console.error('');
    }
    
    return false;
  }
}

function formatEmailContent(reportData: EmailReportData): string {
  const typeLabel = reportData.type === 'bug' ? '🐛 Bug Report' : '💡 Feature Suggestion';
  let content = `${typeLabel}\n`;
  content += `${'='.repeat(50)}\n\n`;
  content += `Title: ${reportData.title}\n\n`;
  content += `Description:\n${reportData.description}\n\n`;
  
  if (reportData.type === 'bug' && reportData.steps) {
    content += `Steps to Reproduce:\n${reportData.steps}\n\n`;
  }
  
  content += `---\n`;
  content += `Browser: ${reportData.browser}\n`;
  content += `Platform: ${reportData.platform}\n`;
  content += `Timestamp: ${reportData.timestamp}\n`;
  
  return content;
}

